/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHERE SIGNING IN TAKES YOU
 *
 * Two rules, and the whole point is that they are different:
 *
 *   A session that ENDED ON ITS OWN — a bookmarked link opened while signed
 *   out, or a session that expired mid-page — returns the person to where they
 *   were headed. Losing their place because a token lapsed would be the system
 *   forgetting on their behalf.
 *
 *   A session somebody DELIBERATELY ENDED starts again at the dashboard.
 *   Pressing sign out is a decision to leave; resuming the previous session's
 *   last screen drops whoever signs in next — often the same person the next
 *   morning, sometimes somebody else on a shared machine — into the middle of
 *   what was being read before.
 *
 * The guard makes that distinction by recording a destination in one case and
 * not the other. This decides what to do with what it recorded.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const DASHBOARD = '/';
export const CHANGE_PASSWORD = '/change-password';

export interface LoginDestinationInput {
  /** Set while the account is still on the password an administrator issued. */
  mustChangePassword: boolean;
  /** What the route guard recorded, if anything. */
  from?: string | null;
}

/**
 * Only a path within this app is ever followed.
 *
 * `from` reaches here through router state, which a crafted link can populate.
 * A value like `//evil.example.com` or `https://evil.example.com` is a valid
 * argument to `navigate` and would send somebody straight off the site,
 * carrying the trust of having just signed in.
 */
function isSafeInternalPath(value: string): boolean {
  if (!value.startsWith('/')) return false;
  // "//host" and "/\host" are both read as protocol-relative by browsers.
  if (value.startsWith('//') || value.startsWith('/\\')) return false;
  return true;
}

export function loginDestination({ mustChangePassword, from }: LoginDestinationInput): string {
  // Somebody still on a temporary password can go nowhere else, whatever they
  // were trying to reach.
  if (mustChangePassword) return CHANGE_PASSWORD;

  if (!from || !isSafeInternalPath(from)) return DASHBOARD;

  // Returning to the login page, or to the screen that forces a password
  // change, is a loop rather than a destination.
  if (from === '/login' || from === CHANGE_PASSWORD) return DASHBOARD;

  return from;
}
